import { navigate } from '@reach/router'

import { constants as lifestyleConstants } from '@cinch/oxd-pages/lifestyle/src'
import { actions as authActions } from '@cinch/oxd-redux/auth/src'
import { actions as lifestyleActions, types as lifestyleTypes } from '@cinch/oxd-redux/lifestyle/src'
import { actions as partnersActions } from '@cinch/oxd-redux/partners/src'
import { actions as profileActions, types as profileTypes } from '@cinch/oxd-redux/profile/src'
import { customEventNames, trackCustomEvent } from '@cinch/oxd-utils/analytics/src'
import {
  clear as clearStorage,
  get as getStorage,
  StorageKeys,
  update as updateStorage,
} from '@cinch/oxd-utils/storage/src'

import { USER_FOUND } from 'redux-oidc'

import { utils } from '@cinch/oxd-pages/lifestyle/src'
import userManager from '../utils/oidc/user-manager'
import { hasFeatureFlag, FEATURES } from '@cinch/oxd-utils/feature-flags/src'
import { identify } from '@cinch/oxd-utils/analytics/src/segment/api-calls'

export default (store: any) => (next: any) => async (action: any) => {
  const { type, payload } = action

  if (type === USER_FOUND) {
    if (payload.access_token) {
      setTimeout(() => {
        store.dispatch(profileActions.getProfile())
        // TODO: This should not be called for all users
        if (payload.profile.idp === 'bca') {
          // for now this will have to do, we want to check a custom claim here.
          store.dispatch(partnersActions.getCustomers())
        }
      }, 0)
    }
  }

  if (type === profileTypes.GET_PROFILE_SUCCESS) {
    store.dispatch(lifestyleActions.getLifestyle())
    if (hasFeatureFlag(FEATURES.CAMBRIDGE)) {
      store.dispatch(profileActions.getDirectFavourites())
    }
    store.dispatch(profileActions.getFavourites())
    store.dispatch(profileActions.getSavedSearches())

    const { oidc } = store.getState()
    const userId = oidc?.user?.profile?.sub
    if (userId && payload) {
      identify(userId, {
        first_name: payload.firstName,
        last_name: payload.lastName,
        email: payload.email,
        phone: payload.phoneNumber,
        preference_sms: payload.preferenceSms,
        preference_email: payload.preferenceEmail,
        preference_digital: payload.preferenceDigital,
        budget: payload.budget,
        finance: payload.finance,
        part_exchange: payload.partExchange,
        delivery_option: payload.deliveryOption,
        latitude: payload.latitude,
        longitude: payload.longitude,
        location: payload.location,
        distance: payload.distance,
      })
    }
  }

  if (type === profileTypes.GET_FAVOURITES_SUCCESS) {
    const favourites = action.payload.favouriteVehicles
    const favourite = getStorage(StorageKeys.favouriteVehicle)
    const alreadyFavourited = favourites.find((fav: any) => fav.vehicleId === favourite.id)
    const hasStorageFavourite = !!favourite && !!favourite.id && !alreadyFavourited

    if (hasStorageFavourite) {
      store.dispatch(profileActions.addLoggedInFavourite(favourite.id, true))
      clearStorage(StorageKeys.favouriteVehicle)
    }
  }

  if (type === profileTypes.GET_DIRECT_FAVOURITES_SUCCESS) {
    const favourites = action.payload.favouriteVehicles
    const favourite = getStorage(StorageKeys.directFavouriteVehicle)
    const alreadyFavourited = favourites.find((fav: any) => fav.vehicleId === favourite.id)
    const hasStorageFavourite = !!favourite && !!favourite.id && !alreadyFavourited

    if (hasStorageFavourite) {
      store.dispatch(profileActions.addLoggedInDirectFavourite(favourite.id, true))
      clearStorage(StorageKeys.directFavouriteVehicle)
    }
  }

  if (type === profileTypes.ADD_FAVOURITE_SUCCESS && action.meta.isLoginFavourite) {
    store.dispatch(profileActions.getFavourites())
  }

  if (type === profileTypes.ADD_DIRECT_FAVOURITE_SUCCESS && action.meta.isLoginFavourite) {
    store.dispatch(profileActions.getDirectFavourites())
  }

  if (type === profileTypes.UPDATE_USER_PROFILE) {
    const updateAnonProfile = () => {
      // Update session storage
      updateStorage(StorageKeys.profile, payload)

      // Update redux
      store.dispatch(profileActions.updateAnonProfile(payload))
    }

    userManager
      .getUser()
      .then((user) => {
        if (user && !user.expired) {
          store.dispatch(profileActions.updateLoggedInProfile(payload))
        } else {
          updateAnonProfile()
        }
      })
      .catch(() => {
        updateAnonProfile()
      })
  }

  if (type === lifestyleTypes.LIFESTYLE_UPDATE_SUCCESS || type === lifestyleTypes.UPDATE_ANON_LIFESTYLE) {
    const page = action.meta.currentPage

    if (page === lifestyleConstants.TOTAL_PAGES) {
      navigate(lifestyleConstants.RECOMMENDED_CARS_URL)
    } else {
      utils.navigateToLifestyleStage(page + 1)
    }
  }

  if (type === lifestyleTypes.UPDATE_LIFESTYLE) {
    const updateAnonLifestyle = () => {
      // Update session storage
      updateStorage(StorageKeys.lifestyle, payload.data)

      // Update redux
      store.dispatch(lifestyleActions.updateAnonLifestyle(payload))
    }

    userManager
      .getUser()
      .then((user) => {
        if (user && !user.expired) {
          store.dispatch(lifestyleActions.updateLoggedInLifestyle(payload))
        } else {
          updateAnonLifestyle()
        }
      })
      .catch(() => {
        updateAnonLifestyle()
      })
  }

  if (type === profileTypes.ADD_FAVOURITE || type === profileTypes.REMOVE_FAVOURITE) {
    userManager
      .getUser()
      .then((user) => {
        if (user && !user.expired) {
          const faveAction =
            type === profileTypes.ADD_FAVOURITE
              ? profileActions.addLoggedInFavourite
              : profileActions.removeLoggedInFavourite

          store.dispatch(faveAction(payload))
        } else {
          trackCustomEvent({ name: customEventNames.unregisteredUserAddToFavourite })
          store.dispatch(profileActions.addSignupFavouriteVehicle(payload))
          store.dispatch(authActions.showAuthModal())
        }
      })
      .catch(() => {
        store.dispatch(profileActions.addSignupFavouriteVehicle(payload))
        store.dispatch(authActions.showAuthModal())
      })
  }

  if (type === profileTypes.ADD_DIRECT_FAVOURITE || type === profileTypes.REMOVE_DIRECT_FAVOURITE) {
    userManager
      .getUser()
      .then((user) => {
        if (user && !user.expired) {
          const faveAction =
            type === profileTypes.ADD_DIRECT_FAVOURITE
              ? profileActions.addLoggedInDirectFavourite
              : profileActions.removeLoggedInDirectFavourite

          store.dispatch(faveAction(payload))
        } else {
          trackCustomEvent({ name: customEventNames.unregisteredUserAddToFavourite })
          store.dispatch(profileActions.addSignupDirectFavouriteVehicle(payload))
          store.dispatch(authActions.showAuthModal())
        }
      })
      .catch(() => {
        store.dispatch(profileActions.addSignupDirectFavouriteVehicle(payload))
        store.dispatch(authActions.showAuthModal())
      })
  }

  if (type === profileTypes.SAVE_SEARCH) {
    const updateAnonSaveSearch = () => {
      updateStorage(StorageKeys.saveSearch, payload)
    }

    userManager
      .getUser()
      .then((user) => {
        if (user && !user.expired) {
          store.dispatch(profileActions.saveSearchLoggedIn(payload.name, payload, payload.location))
        } else {
          updateAnonSaveSearch()
          store.dispatch(authActions.showAuthModal())
        }
      })
      .catch(() => {
        updateAnonSaveSearch()
        store.dispatch(authActions.showAuthModal())
      })
  }

  if (type === profileTypes.SAVE_SEARCH_SUCCESS) {
    // TODO: Remove this once POST call returns the saved search id.
    // Get all saved searches to include the newly created.
    store.dispatch(profileActions.getSavedSearches())
  }

  if (type === profileTypes.GET_SAVED_SEARCHES_SUCCESS) {
    const storedSearch = getStorage(StorageKeys.saveSearch)

    if (storedSearch && storedSearch.name) {
      clearStorage(StorageKeys.saveSearch)
      store.dispatch(profileActions.saveSearchLoggedIn(storedSearch.name, storedSearch, storedSearch.location))
    }
  }

  if (type === profileTypes.DELETE_ACCOUNT_SUCCESS) {
    userManager.revokeAccessToken()
    userManager.removeUser()
    navigate('/')
  }

  if (type === profileTypes.CHANGE_PASSWORD_SUCCESS) {
    store.dispatch(authActions.logout())
  }

  next(action)
}
